import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import {
    MAX_DIAMETER_MM,
    MIN_DIAMETER_MM,
} from '~/engine/tool/tools/contouring-tools/CircleCursorStrokeTool';
import { StrokeMode, deformOptionsState } from '~/state/atoms/toolsAtoms';
import { cmToMm, mmToCm } from '~/utils/units';
import ToolTrayModeToggle from './ToolTrayModeToggle';
import ToolTraySlider from './ToolTraySlider';

interface DeformToolOptionsProps {
    /** Switches shown after the mode, before the diameter. */
    children?: ReactNode;
}

export default function DeformToolOptions({
    children,
}: DeformToolOptionsProps) {
    const intl = useIntl();
    const [deformOptions, setDeformOptions] =
        useRecoilState(deformOptionsState);

    const handleSetDiameter = (diameterCm: number) => {
        const newDiameter = cmToMm(diameterCm);
        if (newDiameter < MIN_DIAMETER_MM || newDiameter > MAX_DIAMETER_MM) {
            return;
        }
        setDeformOptions((options) => ({
            ...options,
            diameter: newDiameter,
        }));
    };

    const handleSetMode = (newMode: StrokeMode) => {
        setDeformOptions((options) => ({
            ...options,
            mode: newMode,
        }));
    };

    const diameterCm = mmToCm(deformOptions.diameter);

    return (
        <>
            <ToolTrayModeToggle
                value={deformOptions.mode}
                onChange={handleSetMode}
                testIdPrefix="deform-mode"
            />
            {children}
            <ToolTraySlider
                label={intl.formatMessage({ id: 'toolOptions.diameter' })}
                value={diameterCm}
                onChange={handleSetDiameter}
                step={0.1}
                min={mmToCm(MIN_DIAMETER_MM)}
                max={mmToCm(MAX_DIAMETER_MM)}
                valueText={diameterCm.toFixed(1)}
                unit="cm"
            />
        </>
    );
}
